import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { CalendarDays } from "lucide-react"
import ToolShell from "../components/ToolShell"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

type Method = "annual" | "monthly" | "banking"

const METHOD_LABELS: Record<Method, string> = {
  annual: "Annual Daily Rate (÷365)",
  monthly: "Monthly Daily Rate (÷days in month)",
  banking: "Banking Method (÷30)",
}

export default function ProratedRentCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [monthlyRent, setMonthlyRent] = useState("")
  const [moveInDate, setMoveInDate] = useState("")
  const [method, setMethod] = useState<Method>("annual")

  const results = useMemo(() => {
    const rent = parseFloat(monthlyRent)
    if (!rent || !moveInDate) return null

    const date = new Date(moveInDate)
    const year = date.getFullYear()
    const month = date.getMonth() // 0-indexed
    const day = date.getDate()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const daysRemaining = daysInMonth - day + 1

    const annualDailyRate = (rent * 12) / 365
    const monthlyDailyRate = rent / daysInMonth
    const bankingDailyRate = rent / 30

    return {
      daysRemaining,
      daysInMonth,
      annual: { rate: annualDailyRate, amount: annualDailyRate * daysRemaining },
      monthly: { rate: monthlyDailyRate, amount: monthlyDailyRate * daysRemaining },
      banking: { rate: bankingDailyRate, amount: bankingDailyRate * daysRemaining },
    }
  }, [monthlyRent, moveInDate])

  const fmt = (n: number) => `${currency}${n.toFixed(2)}`
  const selected = results ? results[method] : null

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Input Panel */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Rent Details</p>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Monthly Rent</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                  <input
                    type="number"
                    min="0"
                    value={monthlyRent}
                    onChange={e => setMonthlyRent(e.target.value)}
                    placeholder="e.g. 1200"
                    className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Move-in Date</label>
                <input
                  type="date"
                  value={moveInDate}
                  onChange={e => setMoveInDate(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Calculation Method</label>
                <select
                  value={method}
                  onChange={e => setMethod(e.target.value as Method)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  {(Object.entries(METHOD_LABELS) as [Method, string][]).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Result Panel */}
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Results</p>

              {!results ? (
                <p className="text-slate-400 text-sm text-center py-10">Enter rent and move-in date to see results</p>
              ) : (
                <>
                  <div className="text-center py-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Prorated Rent Due</p>
                    <p className="text-5xl font-extrabold text-primary">{fmt(selected!.amount)}</p>
                    <p className="text-sm text-slate-500 mt-2">
                      {results.daysRemaining} days × {fmt(selected!.rate)}/day
                    </p>
                  </div>

                  <div className="border-t border-primary/10 pt-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">All Methods Compared</p>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-slate-500">
                          <th className="text-left pb-2">Method</th>
                          <th className="text-right pb-2">Daily Rate</th>
                          <th className="text-right pb-2">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(Object.entries(results) as [string, any][])
                          .filter(([k]) => k !== "daysRemaining" && k !== "daysInMonth")
                          .map(([k, v]) => (
                            <tr key={k} className={k === method ? "font-semibold text-primary" : "text-slate-700"}>
                              <td className="py-1.5 capitalize">{k === "annual" ? "Annual ÷365" : k === "monthly" ? `Monthly ÷${results.daysInMonth}` : "Banking ÷30"}</td>
                              <td className="py-1.5 text-right">{fmt(v.rate)}</td>
                              <td className="py-1.5 text-right">{fmt(v.amount)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between text-sm">
                      <span className="text-slate-500">Full month rent</span>
                      <span className="font-semibold text-slate-900">{fmt(parseFloat(monthlyRent))}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </ToolShell>

      <FormulaBox
        formula={`Annual Daily Rate = (Monthly Rent × 12) ÷ 365\nMonthly Daily Rate = Monthly Rent ÷ Days in Month\nBanking Method = Monthly Rent ÷ 30\n\nProrated Rent = Daily Rate × Days Remaining in Month`}
        description="Days remaining includes the move-in day itself. The most common US method uses the annual daily rate; the monthly daily rate is common in the UK."
      />

      <ToolFAQ
        items={[
          {
            q: "How is prorated rent calculated?",
            a: "Prorated rent is calculated by finding a daily rate and multiplying it by the number of days the tenant will occupy the unit. The daily rate depends on the method: annual (rent × 12 ÷ 365), monthly (rent ÷ days in month), or banking (rent ÷ 30).",
          },
          {
            q: "Which method is most commonly used?",
            a: "In the US, the annual daily rate method (rent × 12 ÷ 365) is most common as it accounts for different month lengths consistently. In the UK, the monthly daily rate (rent ÷ days in month) is more widely used. The banking method (÷30) is a simplified approximation.",
          },
          {
            q: "Can a landlord charge prorated rent?",
            a: "Yes. Prorated rent is standard practice when a tenant moves in mid-month. It fairly reflects the actual number of days occupied. Most lease agreements specify how prorated rent is calculated — always clarify the method before signing.",
          },
        ]}
      />

      <CTABox
        icon={CalendarDays}
        heading="Managing partial payments and invoices?"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
